import React, { RefObject, useRef, useState } from 'react';
import styles from './ThreeDotMenu.module.scss';
import { isEmpty } from '../../utils/utils';
import clsx from 'clsx';
import { useOutsideClick } from '../../utils/constants/hooks';

type ThreeDotMenuProps = {
    menuItems: {
        name: React.ReactNode;
        onClick: Function;
        className?: string;
        keepOpenOnClick?: boolean;
        disabled?: boolean;
    }[];
    color?: React.CSSProperties['color'];
    tableName?: string;
    /** 'small' renders a compact 28px trigger (e.g. inside cards); default keeps the standard 40px */
    size?: 'default' | 'small';
    /** 'horizontal' renders the ⋯ glyph for overflow menus; default keeps the vertical ⋮ */
    glyph?: 'vertical' | 'horizontal';
    /** Lets the caller return focus to the trigger */
    triggerRef?: RefObject<HTMLButtonElement>;
};

const ThreeDotMenu = (props: ThreeDotMenuProps) => {
    const { menuItems, color, size, glyph = 'vertical', triggerRef } = props;
    const isHorizontal = glyph === 'horizontal';
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const parentRef = useRef(null);

    const toggleMenu = () => {
        setIsOpen((prevState) => !prevState);
    };

    // One path for click and keyboard, so `disabled` and `keepOpenOnClick` cannot diverge between them.
    const activateItem = (item: ThreeDotMenuProps['menuItems'][number]) => {
        if (item.disabled) return;
        item.onClick();
        if (!item.keepOpenOnClick) setIsOpen(false);
    };

    useOutsideClick(parentRef, handleClickOutside);

    function handleClickOutside(event: MouseEvent) {
        setIsOpen(false);
    }

    if (isEmpty(props.menuItems)) return <></>;

    return (
        <div className={styles.threeDotMenu} ref={parentRef}>
            <button
                type="button"
                ref={triggerRef}
                className={clsx(styles.icon, { [styles.iconSmall]: size === 'small' })}
                onClick={toggleMenu}
                style={{ color: color ?? undefined }}
                aria-label={isHorizontal ? 'More actions' : 'Open actions menu'}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                {isHorizontal ? '\u22EF' : '\u22EE '}
            </button>
            {isOpen && (
                <div
                    className={clsx(
                        {
                            [styles.ManageEligibilityMenu]: props.tableName === 'ManageEligibilityTable',
                            [styles.CtaActionMenu]: props.tableName === 'CtaActionMenu',
                        },
                        styles.menu,
                    )}
                    ref={menuRef}
                >
                    <ul>
                        {menuItems.map((item, index) => (
                            <li
                                key={index}
                                // Focusable buttons rather than role="menu"/"menuitem": that role owes
                                // arrow-key navigation, a roving tabindex and focus management, and a
                                // half-implemented menu misleads assistive tech more than none. These are
                                // a list of actions, and Tab + Enter/Space is the honest contract.
                                role="button"
                                aria-disabled={item.disabled || undefined}
                                tabIndex={item.disabled ? undefined : 0}
                                onClick={() => activateItem(item)}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    activateItem(item);
                                }}
                                className={clsx({
                                    [styles.Delete]: item.className === 'Delete',
                                    [styles.disabled]: item.disabled,
                                })}
                            >
                                {item.name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ThreeDotMenu;
